import { useCallback, useEffect, useState } from 'react';

type AsyncState<T, E = Error, P extends unknown[] = []> = {
  data: T | null;
  error: E | null;
  loading: boolean;
  run: (...args: P) => Promise<T | undefined>;
  refetch: (...args: P) => Promise<T | undefined>;
};

type UseAsyncOptions = {
  rethrow?: boolean;
};

export function useAsync<T, P extends unknown[] = [], E = Error>(
  fn: (...args: P) => Promise<T>,
  immediate = true,
  options?: UseAsyncOptions
): AsyncState<T, E, P> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<E | null>(null);
  const [loading, setLoading] = useState(false);

  const execute = useCallback(
    async (...args: P) => {
      try {
        setLoading(true);
        setError(null);

        const result = await fn(...args);
        setData(result);
        return result;
      } catch (err) {
        setError(err as E);
        if (options?.rethrow) {
          throw err;
        }
        return undefined;
      } finally {
        setLoading(false);
      }
    },
    [fn, options?.rethrow]
  );

  useEffect(() => {
    if (immediate) {
      void execute(...([] as unknown as P));
    }
  }, [execute, immediate]);

  return {
    data,
    error,
    loading,
    run: execute,
    refetch: execute,
  };
}
